import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import type { Transition } from '@/types/activity';
import type { CreateGenerationJobInput } from '@/types/generation';
import { useCategories, useTags } from '@/hooks/useActivities';

const AGE_GROUP_OPTIONS = [
  { value: '2-3', label: '2–3 years' },
  { value: '3-4', label: '3–4 years' },
  { value: '4-5', label: '4–5 years' },
];

interface GenerationConfigFormProps {
  transitions: Transition[];
  isLoadingTransitions?: boolean;
  onSubmit: (data: CreateGenerationJobInput) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface FormErrors {
  transitionId?: string;
  ageGroup?: string;
  category?: string;
  requestedCount?: string;
  defaultDuration?: string;
}

export default function GenerationConfigForm({
  transitions,
  isLoadingTransitions,
  onSubmit,
  onCancel,
  isLoading,
}: GenerationConfigFormProps) {
  const [categoryId, setCategoryId] = useState<string>('');
  const [transitionId, setTransitionId] = useState<string>('');
  const [ageGroup, setAgeGroup] = useState<string>('3-4');
  const [requestedCount, setRequestedCount] = useState<number>(10);
  const [defaultDuration, setDefaultDuration] = useState<number>(5);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});

  const { data: categoriesData, isLoading: isLoadingCategories } = useCategories();
  const categories = categoriesData?.data || [];
  const { data: tagsData, isLoading: isLoadingTags } = useTags();
  const tags = tagsData?.data || [];

  const inputClassName =
    'w-full px-4 py-3 bg-admin-dark-700 border border-admin-slate-300/10 rounded-md text-admin-slate-400 placeholder-admin-slate-600 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors';
  const selectClassName =
    'w-full px-4 py-3 bg-admin-dark-700 border border-admin-slate-300/10 rounded-md text-admin-slate-400 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors';
  const errorClassName = 'text-red-400 text-sm mt-1';

  const toggleTag = (tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  };

  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    if (!categoryId) errs.category = 'Please select a category';
    if (!transitionId) errs.transitionId = 'Please select a time period';
    if (!ageGroup) errs.ageGroup = 'Please select an age group';
    if (!requestedCount || requestedCount < 1 || requestedCount > 20)
      errs.requestedCount = 'Number of activities must be between 1 and 20';
    if (!defaultDuration || defaultDuration < 1 || defaultDuration > 30)
      errs.defaultDuration = 'Duration must be between 1 and 30 minutes';
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const selectedCategory = categories.find((c) => c.id === categoryId);

    onSubmit({
      transitionId: parseInt(transitionId, 10),
      ageGroup,
      developmentAreas: { type: 'category', value: selectedCategory!.name },
      requestedCount,
      defaultDuration,
      tagIds: selectedTagIds,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card className="bg-admin-dark-800 border-admin-slate-300/10">
        <CardHeader>
          <CardTitle className="text-heading-4 text-admin-slate-400">
            Generation Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category" className="text-admin-slate-400">
              Category <span className="text-red-400">*</span>
            </Label>
            {isLoadingCategories ? (
              <div className="flex items-center gap-2 text-admin-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading categories...
              </div>
            ) : (
              <select
                id="category"
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  if (errors.category) {
                    setErrors((prev) => ({ ...prev, category: undefined }));
                  }
                }}
                className={selectClassName}
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            )}
            {errors.category && (
              <p className={errorClassName}>{errors.category}</p>
            )}
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label className="text-admin-slate-400">
              Tags
            </Label>
            {isLoadingTags ? (
              <div className="flex items-center gap-2 text-admin-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading tags...
              </div>
            ) : tags.length === 0 ? (
              <p className="text-admin-slate-500 text-sm">No tags available</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(parseInt(tag.id, 10))}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      selectedTagIds.includes(parseInt(tag.id, 10))
                        ? 'bg-admin-accent-blue text-white'
                        : 'bg-admin-dark-700 text-admin-slate-400 hover:bg-admin-dark-600 border border-admin-slate-300/10'
                    }`}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Time Period */}
          <div className="space-y-2">
            <Label className="text-admin-slate-400">
              Time Period <span className="text-red-400">*</span>
            </Label>
            {isLoadingTransitions ? (
              <div className="flex items-center gap-2 text-admin-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading time periods...
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {transitions.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setTransitionId(t.id);
                      if (errors.transitionId) {
                        setErrors((prev) => ({ ...prev, transitionId: undefined }));
                      }
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      transitionId === t.id
                        ? 'bg-admin-accent-blue text-white'
                        : 'bg-admin-dark-700 text-admin-slate-400 hover:bg-admin-dark-600 border border-admin-slate-300/10'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
            {errors.transitionId && (
              <p className={errorClassName}>{errors.transitionId}</p>
            )}
          </div>

          {/* Age Group */}
          <div className="space-y-2">
            <Label className="text-admin-slate-400">
              Age Group <span className="text-red-400">*</span>
            </Label>
            <div className="flex gap-3">
              {AGE_GROUP_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setAgeGroup(opt.value);
                    if (errors.ageGroup) {
                      setErrors((prev) => ({ ...prev, ageGroup: undefined }));
                    }
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    ageGroup === opt.value
                      ? 'bg-admin-accent-blue text-white'
                      : 'bg-admin-dark-700 text-admin-slate-400 hover:bg-admin-dark-600 border border-admin-slate-300/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {errors.ageGroup && (
              <p className={errorClassName}>{errors.ageGroup}</p>
            )}
          </div>

          {/* Count and Duration Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Number of Activities */}
            <div className="space-y-2">
              <Label htmlFor="requestedCount" className="text-admin-slate-400">
                Number of Activities <span className="text-red-400">*</span>
              </Label>
              <input
                id="requestedCount"
                type="number"
                min={1}
                max={20}
                value={requestedCount}
                onChange={(e) => {
                  setRequestedCount(parseInt(e.target.value, 10) || 1);
                  if (errors.requestedCount) {
                    setErrors((prev) => ({ ...prev, requestedCount: undefined }));
                  }
                }}
                className={inputClassName}
              />
              {errors.requestedCount && (
                <p className={errorClassName}>{errors.requestedCount}</p>
              )}
            </div>

            {/* Default Duration */}
            <div className="space-y-2">
              <Label htmlFor="defaultDuration" className="text-admin-slate-400">
                Default Duration (minutes) <span className="text-red-400">*</span>
              </Label>
              <input
                id="defaultDuration"
                type="number"
                min={1}
                max={30}
                value={defaultDuration}
                onChange={(e) => {
                  setDefaultDuration(parseInt(e.target.value, 10) || 1);
                  if (errors.defaultDuration) {
                    setErrors((prev) => ({ ...prev, defaultDuration: undefined }));
                  }
                }}
                className={inputClassName}
              />
              {errors.defaultDuration && (
                <p className={errorClassName}>{errors.defaultDuration}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button
          type="submit"
          disabled={isLoading}
          className="bg-admin-accent-blue hover:bg-blue-600 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading Preview...
            </>
          ) : (
            'Preview Prompt →'
          )}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
          className="bg-admin-dark-700 hover:bg-admin-dark-600 text-admin-slate-400"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
